import { ComponentPropsWithoutRef, forwardRef } from "react";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import styles from "./Drawer.module.scss";
import { Text } from "../Text";
import { DrawerCancelButton } from "./DrawerCancelButton";
import cx from "classnames";

/**
 * Props for the DrawerHeader component
 * @extends ComponentPropsWithoutRef<"header">
 */
export type DrawerHeaderProps = ComponentPropsWithoutRef<"header">;

/**
 * Header component for the drawer that displays a title and close button.
 *
 * Features:
 * - Displays the drawer title as a headline
 * - Includes an accessible close button
 * - Sticky positioning at the top of the drawer
 * - Proper semantic HTML structure
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Automatic close functionality
 *
 * @example
 * <Drawer.Header>Settings</Drawer.Header>
 */
export const DrawerHeader = forwardRef<HTMLHeadElement, DrawerHeaderProps>(
  (props, ref) => {
    const { className, children } = props;

    const drawerHeaderClasses = cx(styles.header, className);

    return (
      <header
        className={drawerHeaderClasses}
        data-anv="drawer-header"
        ref={ref}
      >
        <Text variant="headline" el="h1">
          {children}
        </Text>
        <span className={styles["close-container"]}>
          <DrawerCancelButton
            className={styles["close-button"]}
            size="small"
            appearance="ghost"
            aria-label="Close drawer"
            data-anv="drawer-header-close-button"
            icon={Close}
          />
        </span>
      </header>
    );
  },
);

DrawerHeader.displayName = "DrawerHeader";
